import React from 'react'
import { NextSeo } from 'next-seo'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'

const ArchivedGuildTermsPage: NextPageWithLayout = () => {
  return (
    <>
      <NextSeo nofollow noindex />
      <GuildTerms_2024_08_02 />
    </>
  )
}

ArchivedGuildTermsPage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/legal/guild/2024_08_02.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: false,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default ArchivedGuildTermsPage

const orderedListUpperRoman = 'list-[upper-roman] ml-4'
const orderedListUpperAlpha = 'list-[upper-alpha] ml-8'

/**
 *
 * Hey, are you about to make changes to this file? Read this!!!
 *
 * When the this file gets an update, the following process needs to be followed:
 *   - Copy this file and make a new file.
 *   - Name the new file after the date of the udpate using the pattern YYYY-MM-DD.
 *   - Make your changes in the new file. Don't change this copy! It's an official archive!
 *   - Make sure to update the "Updated:" date in the new file.
 *   - Update the name of the exported component in the new file to use the updated date.
 *   - Import and use the new component in the "evergreen" ./index.tsx file.
 *
 */

export const GuildTerms_2024_08_02: React.FC = () => {
  return (
    <div className="bg-white py-20 text-justify text-black">
      <PaddedContainer>
        <div className="flex flex-col gap-8 p-10">
          <h1 className="text-center text-[1.3rem] font-bold md:text-[2rem]">
            <p data-testid="company">Angel Studios</p>
            <p data-testid="terms-of-use-header">ANGEL GUILD TERMS AND CONDITIONS</p>
          </h1>
          <p className="text-center">Updated: August 2, 2024</p>
          <p>
            By joining the Angel Guild, you acknowledge and agree to the following terms and conditions as a member of
            the Angel Guild.
          </p>
          <ol className={orderedListUpperRoman}>
            <li className="mb-4">
              To protect the purpose and integrity of the Angel Guild, Angel Studios reserves the sole and absolute
              right to utilize a confidential formula designed and maintained at its complete discretion to calculate
              Torch and Film ratings. This calculation method is implemented to preserve the accuracy, authenticity, and
              dependability of the Angel Guild in selecting future projects for Angel Studios.
            </li>
            <li className="mb-4">
              Angel Studios may change the practices and rules of the Angel Guild at its discretion to maintain the
              Guild&apos;s objective.
            </li>
            <li className="mb-4">
              As a Guild member, you understand that you will be chosen to view and vote on short video clips, known as
              Torches, from the Guild. You have the option to verify your identity to gain access to review full length
              Films.
            </li>
            <li className="mb-4">
              You acknowledge that not all Guild members will have access to view all Torches and Films due to the
              viewing generation process that Angel Studios has the absolute right to operate.
            </li>
            <li className="mb-4">
              The duration for which a Torch or Film may be viewed in the Angel Guild may vary depending on the time
              required for a reliable Torch/Film rating.
            </li>
            <li className="mb-4">
              Being a member of the Angel Guild is a privilege that may be revoked at any time due to improper behavior,
              such as sharing, copying, or distributing the confidential Torches and Films you will view.
            </li>
            <li className="mb-4">
              <span className="font-semibold">Non-Returnable Items</span>. The following items are categorically
              non-returnable: PIF contributions; movie tickets; Guild membership fees; collectible scenes and frames;
              and other digital assets or goods.
            </li>
            <li className="mb-4">
              <span className="font-semibold">Non-Competition</span>. By accessing or using the Angel Guild, you agree
              that during your use of the Angel Guild and for a period of five (5) years following the termination of
              your access or membership, you will not directly or indirectly engage in the development of any product
              that competes with the Angel Guild developed and owned by Angel Studios. This includes but is not limited
              to developing, distributing, or operating any system or platform that allows users to vote on potential
              film and television series for distribution decisions, without the prior written consent of Angel Studios.
            </li>
            <li className="mb-4">
              <span className="font-semibold">Confidentiality and Non-Use of Information</span>: You acknowledge that
              you may have access to proprietary and confidential information of Angel Studios, including but not
              limited to details of films, television series, and projects submitted to the Angel Guild (“
              <span className="font-semibold">Confidential Information</span>”). You agree that you will not use such
              Confidential Information for any purpose other than your participation in the Angel Guild. Specifically,
              you agree not to use the Confidential Information to:
            </li>
            <ol className={orderedListUpperAlpha}>
              <li className="mb-4">
                Develop, operate, or contribute to any product or service that competes with the Angel Guild or any
                other services provided by Angel Studios.
              </li>
              <li className="mb-4">
                Solicit or entice filmmakers or project owners who have submitted their work to the Angel Guild to move
                their projects to a competing platform or service.
              </li>
            </ol>
            <li className="mb-4">
              <span className="font-semibold">Non-Solicitation</span>: During your use of the Angel Guild and for a
              period of one (1) year following the termination of your access or membership, you agree not to directly
              or indirectly engage, solicit, induce, or recruit any filmmakers or project owners from the Angel Guild to
              join a competing business or service.
            </li>
            <li className="mb-4">
              <span className="font-semibold">Film Viewing and Usage Restrictions</span>: Full-length films made
              available within the Angel Guild are for your personal viewing and evaluation only. These films contain a
              watermark identifying the user viewing the content. You agree not to record, download, or reproduce these
              films by any means, whether digital or otherwise. This includes, but is not limited to, sharing on social
              media, uploading to any online platform, or displaying at public events.
            </li>
            <li className="mb-4">
              <span className="font-semibold">Breach and Remedies</span>: You acknowledge that any breach of these terms
              would cause significant harm to Angel Studios, Inc. and that monetary damages may not be an adequate
              remedy. Therefore, in the event of a breach or threatened breach of these terms, Angel Studios, Inc. shall
              be entitled to seek injunctive relief in addition to any other rights or remedies available at law or in
              equity. Angel Studios, Inc. reserves the right to take all necessary legal action against any individual
              or entity found to be in violation of these terms.
            </li>
          </ol>
        </div>
      </PaddedContainer>
    </div>
  )
}
